<template>
  <header
    :class="['w-full sticky top-0 z-50', isMobile ? 'bg-[#f1f5f9] border-b border-[#c6d3db]' : 'bg-white border-b border-[#e0e3e5] shadow-[0_1px_2px_rgba(0,0,0,0.03)]']"
  >
    <div
      class="flex items-center w-full"
      :class="isMobile && !isDeviceRoute ? 'justify-between h-14 px-4 py-2 gap-2' : 'justify-between h-16 p-4 max-md:p-2'"
    >
      <div
        v-if="!isMobile"
        class="flex items-center max-md:pl-4"
      >
        <router-link to="/home">
          <img
            :src="logo"
            alt="Logo"
            class="h-9 w-auto block"
          >
        </router-link>
      </div>

      <div
        v-if="isMobile && !isDeviceRoute"
        class="flex flex-1 items-center gap-2 min-w-0"
      >
        <button
          ref="profileBtnRef"
          type="button"
          class="inline-flex items-center justify-center w-10 h-10 rounded-md border-none bg-transparent text-[#2b3035] cursor-pointer transition-colors duration-150 hover:bg-[#f2f5f8] focus-visible:bg-[#f2f5f8] focus-visible:outline-none shrink-0"
          :aria-label="$t('settings.profile')"
          :aria-expanded="menuOpen"
          @click.stop="toggleMenu"
        >
          <i
            class="pi pi-user text-[#325F80]"
            style="font-size: 1.5rem;"
          />
        </button>

        <router-link
          to="/home"
          class="flex flex-1 flex-col items-center justify-center min-w-0 px-2 no-underline"
        >
          <span class="text-xs leading-[14px] text-[#717379] overflow-hidden text-ellipsis whitespace-nowrap max-w-full">energielenker connect</span>
          <span class="text-base font-bold leading-[19px] text-[#4D5052] overflow-hidden text-ellipsis whitespace-nowrap max-w-full">{{ $t('page_titles.welcome') }}</span>
        </router-link>

        <div
          class="w-10 h-10 shrink-0"
          aria-hidden="true"
        />
      </div>

      <div
        class="relative flex items-center gap-2 md:justify-end"
        :class="isDeviceRoute ? 'max-md:flex-1 max-md:justify-between' : ''"
      >
        <ActiveDeviceBar
          v-if="isDeviceRoute"
          :device="device"
          :is-mobile="isMobile"
          @exit="exitDevice"
          @edit="goToEditDevice"
        />
        <AppsMenuButton
          v-if="!(isMobile && !isDeviceRoute)"
          :is-mobile="isMobile"
          :is-device-route="isDeviceRoute"
          @open="closeMenu"
        />

        <button
          v-if="!(isMobile && !isDeviceRoute)"
          ref="profileBtnRef"
          v-tooltip.bottom="{ value: $t('settings.profile'), disabled: isMobile }"
          type="button"
          class="inline-flex items-center justify-center w-10 h-10 rounded-full border-none bg-transparent text-[#2b3035] cursor-pointer transition-colors duration-150 hover:bg-[#f2f5f8] focus-visible:bg-[#f2f5f8] focus-visible:outline-none"
          :aria-label="$t('settings.profile')"
          :aria-expanded="menuOpen"
          @click.stop="toggleMenu"
        >
          <i
            class="pi pi-user"
            style="font-size: 1.375rem;"
          />
        </button>
        <transition name="backdrop">
          <div
            v-if="menuOpen && isMobile"
            class="fixed inset-0 bg-black/40 z-[90]"
            aria-hidden="true"
            @click="closeMenu"
          />
        </transition>
        <transition :name="isMobile ? 'slide' : 'fade'">
          <div
            v-if="menuOpen"
            ref="menuRef"
            class="box-border fixed inset-y-0 left-0 h-full w-[90%] overflow-y-auto overflow-x-hidden rounded-none border border-[#e0e3e5] md:absolute md:inset-y-auto md:top-[calc(100%+8px)] md:left-auto md:right-0 md:h-auto md:w-[353px] md:max-w-none md:overflow-visible md:rounded-md flex flex-col items-start p-3 lg:p-4 gap-3 bg-white  z-[100]"
            role="menu"
            @touchstart="onMenuTouchStart"
            @touchend="onMenuTouchEnd"
          >
            <ProfileMenuPanel
              :is-mobile="isMobile"
              :has-admin-access="authStore.hasAdminAccess"
              :admin-sections="adminSections"
              :full-name="fullName"
              :company="company"
              :role-label="roleLabel"
              @navigate="navigate"
              @logout="onLogout"
              @close="closeMenu"
            />
          </div>
        </transition>
      </div>
    </div>
  </header>
</template>

<script lang="ts">
import { defineComponent, ref, computed, onMounted, onBeforeUnmount, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useAuthStore } from '@/stores/authStore';
import { useAdminSections } from '@/composables/useAdminSections';
import { useProfile } from '@/composables/useProfile';
import ActiveDeviceBar from '@/components/header/ActiveDeviceBar.vue';
import AppsMenuButton from '@/components/header/AppsMenuButton.vue';
import ProfileMenuPanel from '@/components/header/ProfileMenuPanel.vue';
import logo from '@/assets/logo.svg';
import Tooltip from 'primevue/tooltip';
import apiClient from '@/services/axios';

const ROLE_PRIORITY = [
  'ROLE_SUPER_ADMIN',
  'ROLE_ADMIN',
  'ROLE_TENANT_OWNER',
  'ROLE_TENANT_ADMIN',
  'ROLE_ORG_ADMIN',
  'ROLE_TECHNICIAN',
  'ROLE_USER',
];

const normalizeRoles = (raw: unknown): string[] => {
  if (!raw) return [];
  if (Array.isArray(raw)) return raw.map((r) => String(r));
  if (typeof raw === 'object') {
    return Object.values(raw as Record<string, unknown>).map((r) => String(r));
  }
  return [String(raw)];
};

const primaryRole = (roles: string[]): string => {
  const upper = roles.map((r) => r.toUpperCase());
  for (const candidate of ROLE_PRIORITY) {
    if (upper.includes(candidate)) {
      return candidate.replace(/^ROLE_/, '').toLowerCase().replace(/_/g, '-');
    }
  }
  const first = upper[0] ?? '';
  return first.replace(/^ROLE_/, '').toLowerCase().replace(/_/g, '-');
};

export default defineComponent({
  name: 'TopBarComponent',
  components: {
    ActiveDeviceBar,
    AppsMenuButton,
    ProfileMenuPanel,
  },
  directives: {
    tooltip: Tooltip,
  },
  setup() {
    const route = useRoute();
    // Detect if current route is /device/:deviceId
    const isDeviceRoute = computed(() => route.name === 'device');
    const isMobile = ref(typeof window !== 'undefined' && window.innerWidth <= 768);
    const onResize = () => {
      isMobile.value = window.innerWidth <= 768;
    };
  
    // we need to revise this later on
    const exitDevice = () => {
      if (route.query?.from === 'admin-tenant' && route.query?.tenantId) {
        router.push({
          name: 'admin-tenant-overview',
          params: { id: route.query.tenantId as string },
        });
      } else if (route.query?.from === 'admin') {
        router.push({ name: 'admin-devices' });
      } else {
        router.push({ name: 'home' });
      }
    };

    const device = ref<{ id?: string; name?: string; type?: string } | null>(null);

    const fetchDevice = async (deviceId: string) => {
      try {
        const { data } = await apiClient.get(`/devices/${deviceId}`);
        device.value = data ?? null;
      } catch (error) {
        console.error('Failed to load device:', error);
        device.value = null;
      }
    };

    watch(
      () => route.params.deviceId,
      (deviceId) => {
        if (isDeviceRoute.value && deviceId) {
          fetchDevice(deviceId as string);
        } else {
          device.value = null;
        }
      },
      { immediate: true }
    );

    const goToEditDevice = () => {
      const deviceId = route.params.deviceId as string;
      if (!deviceId) return;
      router.push({
        name: 'edit-device',
        params: {
          id: deviceId,
        },
      });
    };
    const authStore = useAuthStore();
    const adminSections = useAdminSections();
    const router = useRouter();
    const { logout } = useProfile();
    const { t, te } = useI18n();

    const roleLabel = computed(() => {
      const role = primaryRole(normalizeRoles(authStore.user?.roles));
      if (!role) return '';
      const normalized = role.toLowerCase().replace(/[\s-]+/g, '_');
      const key = `admin.tenant_overview.roles.${normalized}`;
      return te(key) ? t(key) : role;
    });

    const menuOpen = ref(false);
    const menuRef = ref<HTMLElement | null>(null);
    const profileBtnRef = ref<HTMLElement | null>(null);

    const fullName = computed(() => authStore.fullName);
    const company = computed(
      () =>
        (authStore.user as any)?.company ||
        (authStore.user as any)?.organization ||
        (authStore.user as any)?.organisation ||
        ''
    );

    const closeMenu = () => { menuOpen.value = false; };
    const toggleMenu = () => {
      menuOpen.value = !menuOpen.value;
    };

    const SWIPE_CLOSE_THRESHOLD = 60;
    const SWIPE_VERTICAL_RESTRAINT = 75;
    const touchStartX = ref(0);
    const touchStartY = ref(0);

    const onMenuTouchStart = (e: TouchEvent) => {
      const touch = e.touches[0];
      touchStartX.value = touch.clientX;
      touchStartY.value = touch.clientY;
    };

    const onMenuTouchEnd = (e: TouchEvent) => {
      if (!isMobile.value) return;
      const touch = e.changedTouches[0];
      const deltaX = touch.clientX - touchStartX.value;
      const deltaY = touch.clientY - touchStartY.value;
      if (deltaX < -SWIPE_CLOSE_THRESHOLD && Math.abs(deltaY) < SWIPE_VERTICAL_RESTRAINT) {
        closeMenu();
      }
    };

    const navigate = (path: string) => {
      closeMenu();
      router.push(path);
    };

    const goToNotifications = () => {
      router.push('/notifications');
    };

    const onLogout = async () => {
      closeMenu();
      await logout();
    };

    const onDocumentClick = (e: MouseEvent) => {
      const target = e.target as Node;
      const targetEl = target as HTMLElement;
      if (targetEl.closest && targetEl.closest('.p-dialog, .p-dialog-mask, .p-popover, .p-overlay')) {
        return;
      }
      if (document.querySelector('.p-dialog-mask, .p-popover-mask')) {
        return;
      }
      if (
        menuOpen.value &&
        menuRef.value &&
        !menuRef.value.contains(target) &&
        profileBtnRef.value &&
        !profileBtnRef.value.contains(target)
      ) {
        closeMenu();
      }
    };

    const onEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { 
        closeMenu(); 
        //closeApps(); 
    }
    };

    watch([menuOpen, isMobile], ([isOpen, mobile]) => {
      if (isOpen && mobile) {
        document.body.style.overflow = 'hidden';
      } else {
        document.body.style.overflow = '';
      }
    });

    onMounted(() => {
      document.addEventListener('click', onDocumentClick);
      document.addEventListener('keydown', onEsc);
      window.addEventListener('resize', onResize);
    });

    onBeforeUnmount(() => {
      document.removeEventListener('click', onDocumentClick);
      document.removeEventListener('keydown', onEsc);
      window.removeEventListener('resize', onResize);
      document.body.style.overflow = '';
    });

    return {
      authStore,
      adminSections,
      menuOpen,
      menuRef,
      profileBtnRef,
      fullName,
      company,
      roleLabel,
      logo,
      toggleMenu,
      closeMenu,
      onMenuTouchStart,
      onMenuTouchEnd,
      navigate,
      onLogout,
      isDeviceRoute,
      isMobile,
      exitDevice,
      goToEditDevice,
      device,
    };
    },
  
  
});


</script>

<style lang="scss" scoped>
.backdrop-enter-active,
.backdrop-leave-active {
  transition: opacity 0.2s ease;
}

.backdrop-enter-from,
.backdrop-leave-to {
  opacity: 0;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.12s ease, transform 0.12s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
  transform: translateY(-4px);
}

.slide-enter-active,
.slide-leave-active {
  transition: transform 0.25s ease;
}

.slide-enter-from,
.slide-leave-to {
  transform: translateX(-100%);
}
</style>
